import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, MailPlus, Sparkles, Bot } from "lucide-react";
import { TIPOS_CORREO, CANALES } from "@/lib/constants";

export default function IngestEmailDialog({ open, onOpenChange, onProcessed }) {
  const [form, setForm] = useState({ remitente: "", asunto: "", cuerpo: "", buzon_email: "" });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const reset = () => {
    setForm({ remitente: "", asunto: "", cuerpo: "", buzon_email: "" });
    setPreview(null);
  };

  const vistaPrevia = async () => {
    if (!form.cuerpo.trim()) {
      toast.error("Pegá el cuerpo del correo.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/captura/test-parse", form);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const procesar = async () => {
    if (!form.cuerpo.trim()) {
      toast.error("Pegá el cuerpo del correo.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/captura/ingest", form);
      if (data?.duplicado) {
        toast.info("Ese correo ya había sido procesado.");
      } else if (data?.omitido_bokun) {
        // PROMPT 114: aca no se hizo nada; antes decia "Reserva creada automaticamente".
        toast.info("Correo de una cuenta de Bókun: no se hizo nada, porque sus reservas, cambios y cancelaciones entran por la sincronización.");
      } else if (data?.motivo_accion) {  // PROMPT 115: tambien cuando quedo vinculado a una reserva
        toast.info(data.motivo_accion, { duration: 12000 });
      } else if (data?.requiere_revision) {
        toast.warning("Reserva enviada a la Bandeja a revisar.");
      } else if (data?.tipo_correo !== "reserva_nueva") {
        toast.success("Correo vinculado como acción a revisar.");
      } else {
        toast.success("Reserva creada automáticamente.");
      }
      onOpenChange(false);
      reset();
      onProcessed?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ingest-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MailPlus className="h-5 w-5" /> Pegar / reenviar correo
          </DialogTitle>
          <DialogDescription>
            Pegá un correo de reserva (GetYourGuide u otro). El sistema lo parsea con reglas e IA y crea la
            reserva o la manda a la bandeja a revisar.
            <span className="block mt-1" data-testid="ingest-ayuda-cuerpo">
              Alcanza con pegar el cuerpo del correo. Remitente y asunto son opcionales.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Remitente (opcional)</Label>
              <Input
                value={form.remitente}
                onChange={(e) => set({ remitente: e.target.value })}
                placeholder="GetYourGuide <noreply@getyourguide.com>"
                data-testid="ingest-remitente-input"
              />
              <p className="text-[11px] text-slate-500">
                Quién manda el correo (GetYourGuide, Bókun, la agencia). No pongas el email del cliente: si lo ponés, se toma como un mensaje del cliente y no como una reserva.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Buzón de origen (opcional)</Label>
              <Input
                value={form.buzon_email}
                onChange={(e) => set({ buzon_email: e.target.value })}
                placeholder="intertravelnyc@gmail.com"
                data-testid="ingest-buzon-input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Asunto (opcional)</Label>
            <Input
              value={form.asunto}
              onChange={(e) => set({ asunto: e.target.value })}
              placeholder="Booking - S1234567 - GYGXXXXXXXX"
              data-testid="ingest-asunto-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Cuerpo del correo</Label>
            <Textarea
              value={form.cuerpo}
              onChange={(e) => set({ cuerpo: e.target.value })}
              rows={8}
              placeholder="Pegá aquí el contenido del correo..."
              data-testid="ingest-cuerpo-input"
            />
          </div>

          {preview && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="ingest-preview">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-slate-500" />
                <span className="font-medium text-slate-800">Vista previa de extracción</span>
                {preview.uso_llm && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded">
                    <Bot className="h-3 w-3" /> IA
                  </span>
                )}
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
                <Item k="Canal" v={CANALES[preview.canal] || preview.canal} />
                <Item k="Tipo" v={TIPOS_CORREO[preview.tipo_correo] || preview.tipo_correo} />
                <Item k="Tour" v={preview.tour?.nombre || "— (sin match)"} />
                <Item k="Código" v={preview.parsed?.codigo_booking || "—"} />
                <Item k="Pasajero" v={`${preview.parsed?.nombre || ""} ${preview.parsed?.apellido || ""}`.trim() || "—"} />
                <Item k="Fecha/Hora" v={`${preview.parsed?.fecha || "—"} ${preview.parsed?.hora || ""}`} />
                <Item k="Pax" v={`${preview.parsed?.adultos || 0} ad · ${preview.parsed?.ninos || 0} niñ · ${preview.parsed?.infantes || 0} inf`} />
                <Item k="Modalidad" v={preview.parsed?.modalidad} />
                <Item k="Agencia" v={preview.agencia?.nombre || (preview.requiere_asignacion_agencia ? "— (asignar)" : "—")} />
                {preview.facturada && (
                  <Item k="Facturación" v={`Facturada · ref. $${Number(preview.monto_facturado_agencia || 0).toFixed(2)}`} />
                )}
                <Item k="Confianza" v={`${Math.round((preview.confianza || 0) * 100)}%`} />
                <Item
                  k="Destino"
                  v={preview.omitido_bokun
                    ? "Nada (cuenta de Bókun)"
                    : preview.motivo_accion
                      ? "Acciones, sin cambios automáticos"
                      : preview.requiere_revision ? "Bandeja a revisar" : "Alta directa"}
                />
              </dl>
              {preview.omitido_bokun && (
                <p className="text-[12px] text-amber-700 mt-2">⚠ Es de una cuenta de Bókun: sus reservas, cambios y cancelaciones entran por la sincronización, no por este correo.</p>
              )}
              {preview.motivo_accion && (
                <p className="text-[12px] text-amber-700 mt-2">⚠ {preview.motivo_accion}</p>
              )}
              {preview.motivo_revision && (
                <p className="text-[12px] text-amber-700 mt-2">⚠ {preview.motivo_revision}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={vistaPrevia} disabled={loading || !form.cuerpo.trim()} data-testid="ingest-preview-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Vista previa
          </Button>
          <Button onClick={procesar} disabled={loading || !form.cuerpo.trim()} data-testid="ingest-process-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Procesar correo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Item({ k, v }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] uppercase tracking-wide text-slate-400">{k}</dt>
      <dd className="text-slate-800 font-medium truncate">{v}</dd>
    </div>
  );
}
